import { useParams } from 'react-router-dom';
import useHTMLLinkClickHandler from 'app/hooks/useHTMLLinkClickHandler';
import CustomScrollbar from 'app/components/CustomScrollbar';
import { useEffect, useRef } from 'react';

import useImportantSafetyInformation from '../../../hooks/useImportantSafetyInformation';
import Header from '../../../components/Header';
import useFetchConsent from '../../../hooks/fetch/useFetchConsent';
import Spinner from '../../../components/Spinner';
import TailorDynamicContent from '../../../components/OstroDynamicContent';
import useScrollProgress from '../hooks/useScrollProgress';
import useTailorTracking from '../hooks/useTailorTracking';

function TailorInfo() {
  const paramType = useParams().type as 'isi' | 'privacy' | 'terms';
  const importantSafetyInformation = useImportantSafetyInformation();
  const { captureScrollProgress } = useTailorTracking();
  const handleHTMLLinkClick = useHTMLLinkClickHandler();
  const { data: consentData, isLoading } = useFetchConsent({
    enabled: paramType !== 'isi',
  });
  const containerRef = useRef<HTMLDivElement | null>(null);
  const progress = useScrollProgress(containerRef);

  useEffect(() => {
    if (progress > 0) {
      captureScrollProgress(progress);
    }
  }, [progress, captureScrollProgress]);

  const title = (() => {
    switch (paramType) {
      case 'isi':
        return importantSafetyInformation?.preview?.title;
      case 'privacy':
        return 'Privacy Policy';
      case 'terms':
        return 'Terms of Use';
      default:
        return '';
    }
  })();

  const content = (() => {
    switch (paramType) {
      case 'isi':
        return importantSafetyInformation?.preview?.content;
      case 'privacy':
        return consentData?.ostro_privacy_policy;
      case 'terms':
        return consentData?.ostro_terms_of_use;
      default:
        return '';
    }
  })();

  return (
    <CustomScrollbar
      id="ostro-tailor-info-container"
      dataTestId={`ostro-tailor-info-container-${paramType}`}
      containerRef={containerRef}
      rememberScrollPosition
      className="px-6 py-7"
    >
      <Header showIndicationSelector={false} />
      {isLoading ? (
        <div className="flex justify-center mt-16">
          <Spinner />
        </div>
      ) : (
        <>
          <div
            data-testid="info-title"
            className="font-bold my-4 info-title"
            onClick={handleHTMLLinkClick}
            dangerouslySetInnerHTML={{
              __html: title,
            }}
          />
          <TailorDynamicContent
            content={content}
            onClick={handleHTMLLinkClick}
          />
        </>
      )}
    </CustomScrollbar>
  );
}

export default TailorInfo;
